/** Owner-side console for the Dual-OTP committee invite flow.
 *
 *  Owner mints an invite → server generates two 6-digit OTPs (email +
 *  phone) → console surfaces both codes (dev mode) + a copyable
 *  verify URL to share with the candidate.
 */
import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { KeyRound, Copy, Check, Users, Plus, Send } from "lucide-react";
import { toast } from "sonner";

export default function CommitteeInvitePanel({ org, onChange, accent = "#F5B82E" }) {
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  // Bump this key each time the dialog closes so the internal state
  // (form fields + minted-invite view) resets between mints.
  const [dialogKey, setDialogKey] = useState(0);

  useEffect(() => {
    api.get("/meta/committee-roles")
       .then(({ data }) => setRoles(data.roles || []))
       .catch(() => setRoles([]));
  }, []);

  const seated = (org.committee_members || []).length;
  const target = org.committee_size || 7;

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) setDialogKey((k) => k + 1);
  };

  return (
    <div className="mt-6 cs-card rounded-2xl p-6" data-testid="committee-invite-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center"
               style={{ background: `${accent}20`, color: accent }}>
            <Users size={16} />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Committee</div>
            <div className="font-display text-xl uppercase tracking-tight" data-testid="committee-count">
              {seated} / {target} seated
            </div>
          </div>
        </div>
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em]"
                    style={{ background: accent, color: "#0b0b0d" }}
                    data-testid="mint-committee-invite-btn">
              <Plus size={12} className="mr-1.5" /> Invite Member
            </Button>
          </DialogTrigger>
          <MintInviteDialog key={dialogKey} roles={roles}
                            onDone={() => { handleOpenChange(false); onChange?.(); }} />
        </Dialog>
      </div>
    </div>
  );
}

function MintInviteDialog({ roles, onDone }) {
  const [role, setRole]   = useState("");
  const [name, setName]   = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy]   = useState(false);
  const [result, setResult] = useState(null);

  const canSubmit = role && name.trim().length >= 2 && email && phone.trim().length >= 6 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post("/associations/committee/invite", {
        role,
        invitee_name:  name.trim(),
        invitee_email: email.trim(),
        invitee_phone: phone.trim(),
      });
      setResult(data);
      toast.success("Invite minted — codes generated for email + phone");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not mint invite");
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return <MintedInviteView result={result} onClose={onDone} />;
  }

  return (
    <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-lg" data-testid="mint-committee-invite-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight">Invite Committee Member</DialogTitle>
        <DialogDescription className="text-white/55 text-xs">
          Server mints two distinct 6-digit OTPs — one goes to email, one to phone. Both must be verified to seat.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Committee Role *</Label>
          <select value={role} onChange={(e) => setRole(e.target.value)}
                  className="mt-2 w-full h-10 rounded-md bg-black/40 border border-white/15 px-3 text-white text-sm"
                  data-testid="invite-role-select">
            <option value="">Pick a role…</option>
            {roles.map(r => (
              <option key={r.key} value={r.key}>{r.label}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Full Name *</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
                 className="mt-2 h-10 bg-black/40 border-white/15" data-testid="invite-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Email *</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   className="mt-2 h-10 bg-black/40 border-white/15"
                   data-testid="invite-email-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Phone *</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                   placeholder="+91 …" className="mt-2 h-10 bg-black/40 border-white/15"
                   data-testid="invite-phone-input" />
          </div>
        </div>
        <Button onClick={submit} disabled={!canSubmit}
                className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em]"
                data-testid="invite-submit">
          {busy ? "Minting…" : <><Send size={13} className="mr-2" /> Mint Dual-OTP Invite</>}
        </Button>
      </div>
    </DialogContent>
  );
}

function MintedInviteView({ result, onClose }) {
  const [copied, setCopied] = useState("");
  const copy = (val, key) => {
    navigator.clipboard.writeText(val).then(() => {
      setCopied(key);
      toast.success("Copied");
      setTimeout(() => setCopied(""), 2000);
    }).catch((err) => {
      console.warn("[MintedInviteView] clipboard denied:", err?.message);
      toast.error("Clipboard permission denied — copy manually");
    });
  };
  const shareUrl = result.share_url?.startsWith("http")
    ? result.share_url : `${window.location.origin}${result.share_url}`;
  const dev = result.dev_peek || {};
  const emailProvider = result.email_provider || "mock";
  const smsProvider   = result.sms_provider   || "mock";
  return (
    <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-lg" data-testid="minted-invite-view">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight">
          Invite Ready — {result.role_label}
        </DialogTitle>
        <DialogDescription className="text-white/55 text-xs">
          {result.invitee_name} · {result.invitee_email} · {result.invitee_phone}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <CodeRow label="Email OTP" value={dev.email_otp}
                 provider={emailProvider}
                 testid="email-otp-code"
                 providerTestid="email-provider-badge"
                 onCopy={() => copy(dev.email_otp, "email")}
                 copied={copied === "email"} />
        <CodeRow label="Phone OTP" value={dev.phone_otp}
                 provider={smsProvider}
                 testid="phone-otp-code"
                 providerTestid="sms-provider-badge"
                 onCopy={() => copy(dev.phone_otp, "phone")}
                 copied={copied === "phone"} />
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Verify URL</Label>
          <div className="mt-2 flex gap-2">
            <div className="flex-1 h-10 flex items-center px-3 rounded-md bg-black/40 border border-white/15 text-[11px] font-mono text-white/75 truncate"
                 data-testid="invite-share-url">{shareUrl}</div>
            <Button onClick={() => copy(shareUrl, "url")}
                    className="h-10 px-4 rounded-md bg-white/10 hover:bg-white/15 text-white text-[10px] uppercase tracking-[0.22em]"
                    data-testid="copy-share-url">
              {copied === "url" ? <Check size={12} /> : <Copy size={12} />}
            </Button>
          </div>
        </div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-white/40 border-t border-white/10 pt-3">
          Codes valid 48h · Delivery: <ProviderPill provider={emailProvider} /> email · <ProviderPill provider={smsProvider} /> SMS
        </div>
        <Button onClick={onClose} className="w-full h-10 rounded-full text-[10px] uppercase tracking-[0.22em] bg-white/10 hover:bg-white/15 text-white">
          Done
        </Button>
      </div>
    </DialogContent>
  );
}

function CodeRow({ label, value, provider, providerTestid, onCopy, copied, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</Label>
        <ProviderPill provider={provider} testid={providerTestid} />
      </div>
      <div className="mt-2 flex gap-2">
        <div className="flex-1 h-12 flex items-center justify-center rounded-md bg-black/40 border border-white/15 font-mono text-2xl tracking-[0.4em] text-white"
             data-testid={testid}>
          <KeyRound size={16} className="mr-3 text-[#F5B82E]" />
          {value || "······"}
        </div>
        <Button onClick={onCopy}
                className="h-12 px-4 rounded-md bg-white/10 hover:bg-white/15 text-white text-[10px] uppercase tracking-[0.22em]">
          {copied ? <Check size={13} /> : <Copy size={13} />}
        </Button>
      </div>
    </div>
  );
}

/** Small pill showing the actual delivery route the server used
 *  (resend / twilio / india_dlt / mock). Colour-codes at a glance. */
function ProviderPill({ provider, testid }) {
  const label = provider || "mock";
  const tone =
    label === "mock"      ? "bg-white/5 border-white/10 text-white/50" :
    label === "india_dlt" ? "bg-[#4EC1E4]/10 border-[#4EC1E4]/30 text-[#4EC1E4]" :
    label === "twilio"    ? "bg-[#F22F46]/10 border-[#F22F46]/30 text-[#F22F46]" :
    label === "resend"    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" :
                            "bg-amber-500/10 border-amber-500/30 text-amber-300";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] tracking-[0.2em] uppercase border ${tone}`}
          data-testid={testid}>
      via {label.replace("_", "-")}
    </span>
  );
}
